<script lang="ts">
  import { inertia, usePage, router } from "@inertiajs/svelte";
  import * as Sidebar from "$lib/components/ui/sidebar/index.js";
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu/index.js";
  import {
    Home,
    Images,
    Languages,
    Globe,
    FileText,
    BookOpen,
    Tag,
    LayoutTemplate,
    Settings,
    LogOut,
    ChevronsUpDown,
    Plus,
    ClipboardList,
    Link // 💡 引入短链接图标
  } from "lucide-svelte";
  import { toast } from "svelte-sonner";

  const page = usePage();

  let currentSite = $derived(page.props?.current_site as any);
  let sites = $derived(page.props?.available_sites || []);
  let currentTenant = $derived(page.props?.current_tenant as any);
  let url = $derived(page.url);


  // 💡 Svelte 5 衍生消费语言包
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.sidebar || {});

  console.log(`语言包：${t.brand_suffix || ''}`);
  function switchSite(siteId: number) {
    if (siteId === currentSite?.id) return;
    
    // 💡 动态提示词
    const toastId = toast.loading(t.switching || "Switching...");

    router.post('/sites/switch', { site_id: siteId }, {
      preserveState: false,
      onSuccess: () => toast.dismiss(toastId),
      onError: () => toast.dismiss(toastId),
    });
  }

  let isMultiSiteAllowed = $derived((currentTenant?.max_sites || 1) > 1);
</script>

<Sidebar.Root collapsible="icon" class="border-r">
  <Sidebar.Header>
    <Sidebar.Menu>
      <Sidebar.MenuItem>
        {#if isMultiSiteAllowed}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger class="w-full">
              {@render SiteButton(currentSite)}
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              class="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              sideOffset={4}
            >
              <DropdownMenu.Label class="text-xs text-muted-foreground">{t.switch_site}</DropdownMenu.Label>
              {#each sites as site}
                <DropdownMenu.Item 
                  class="gap-2 p-2 cursor-pointer" 
                  onclick={() => switchSite(site.id)}
                >
                  <div class="flex size-6 shrink-0 items-center justify-center rounded-sm border bg-primary text-primary-foreground text-[10px] font-bold">
                    {site.name[0]}
                  </div>
                  <span class="flex-1 truncate {site.id === currentSite?.id ? 'font-bold text-primary' : ''}">
                    {site.name}
                  </span>
                  {#if site.id === currentSite?.id}
                    <div class="size-1.5 rounded-full bg-primary"></div>
                  {/if}
                </DropdownMenu.Item>
              {/each}

              <DropdownMenu.Separator />
              <DropdownMenu.Item class="gap-2 p-2" onclick={() => router.get('/sites/new')}>
                <div class="flex size-6 items-center justify-center rounded-md border bg-background">
                  <Plus class="size-4" />
                </div>
                <div class="font-medium text-muted-foreground">{t.create_site}</div>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        {:else}
          {@render SiteButton(currentSite, false)}
        {/if}
      </Sidebar.MenuItem>
    </Sidebar.Menu>
  </Sidebar.Header>

  {#key currentSite?.id}
  <Sidebar.Content>
    <Sidebar.Group>
      <Sidebar.GroupLabel></Sidebar.GroupLabel>
      <Sidebar.GroupContent>
        <Sidebar.Menu>
          <Sidebar.MenuItem>
            <Sidebar.MenuButton asChild isActive={url.startsWith("/dashboard")} tooltip={t.menu_home}>
              <a href="/dashboard" use:inertia class="flex items-center gap-3 w-full">
                <Home class="size-4 shrink-0" />
                <span class="truncate">{t.menu_home}</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        </Sidebar.Menu>
      </Sidebar.GroupContent>
    </Sidebar.Group>
    
    <Sidebar.Group>
      <Sidebar.GroupLabel>{t.group_content}</Sidebar.GroupLabel>
      <Sidebar.GroupContent>
        <Sidebar.Menu>
          <Sidebar.MenuItem>
            <Sidebar.MenuButton asChild isActive={url.startsWith("/medias")} tooltip={t.menu_media}>
              <a href="/medias" use:inertia class="flex items-center gap-3 w-full">
                <Images class="size-4 shrink-0" />
                <span class="truncate">{t.menu_media}</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
          
          <Sidebar.MenuItem>
            <Sidebar.MenuButton asChild isActive={url.startsWith("/categories")} tooltip={t.menu_category}>
              <a href="/categories" use:inertia class="flex items-center gap-3 w-full">
                <Tag class="size-4 shrink-0" />
                <span class="truncate">{t.menu_category}</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          <Sidebar.MenuItem>
            <Sidebar.MenuButton asChild isActive={url.startsWith("/posts")} tooltip={t.menu_post}>
              <a href="/posts" use:inertia class="flex items-center gap-3 w-full">
                <BookOpen class="size-4 shrink-0" />
                <span class="truncate">{t.menu_post}</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          <!-- 💡 新增：短链接菜单入口 -->
          <Sidebar.MenuItem>
            <Sidebar.MenuButton asChild isActive={url.startsWith("/short_urls")} tooltip={t.menu_short_urls || '短链接'}>
              <a href="/short_urls" use:inertia class="flex items-center gap-3 w-full">
                <Link class="size-4 shrink-0" />
                <span class="truncate">{t.menu_short_urls || '短链接'}</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          <Sidebar.MenuItem>
            <Sidebar.MenuButton asChild isActive={url.startsWith("/inquiries")} tooltip={t.menu_inquiry}>
              <a href="/inquiries" use:inertia class="flex items-center gap-3 w-full">
                <ClipboardList class="size-4 shrink-0" />
                <span class="truncate">{t.menu_inquiry}</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        </Sidebar.Menu>
      </Sidebar.GroupContent>
    </Sidebar.Group>

    <Sidebar.Group>
      <Sidebar.GroupLabel>{t.group_site}</Sidebar.GroupLabel>
      <Sidebar.GroupContent>
        <Sidebar.Menu>
          <Sidebar.MenuItem>
            <Sidebar.MenuButton asChild isActive={url.startsWith("/sites") && !url.endsWith("/edit") && url !== "/sites"} tooltip={t.menu_site_manage}>
              <a href="/site/edit" use:inertia class="flex items-center gap-3 w-full">
                <Globe class="size-4 shrink-0" />
                <span class="truncate">{t.menu_site_manage}</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>

          <Sidebar.MenuItem>
            <Sidebar.MenuButton asChild isActive={url.startsWith("/pages")} tooltip={t.menu_page}>
              <a href="/pages" use:inertia class="flex items-center gap-3 w-full">
                <FileText class="size-4 shrink-0" />
                <span class="truncate">{t.menu_page}</span>
              </a>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        </Sidebar.Menu>
      </Sidebar.GroupContent>
    </Sidebar.Group>

    {#if isMultiSiteAllowed}
      <Sidebar.Group class="mt-auto">
        <Sidebar.GroupContent>
          <Sidebar.Menu>
            <Sidebar.MenuItem>
              <Sidebar.MenuButton asChild isActive={url === "/sites"} tooltip={t.menu_site_settings}>
                <a href="/sites" use:inertia class="flex items-center gap-3 w-full">
                  <Settings class="size-4 shrink-0" />
                  <span class="truncate">{t.menu_site_settings}</span>
                </a>
              </Sidebar.MenuButton>
            </Sidebar.MenuItem>
          </Sidebar.Menu>
        </Sidebar.GroupContent>
      </Sidebar.Group>
    {/if}
  </Sidebar.Content>
  {/key}
  
  <Sidebar.Footer>
    <Sidebar.Menu>
      <Sidebar.MenuItem>
        <Sidebar.MenuButton tooltip={t.menu_logout} onclick={() => router.delete('/logout')}>
          <LogOut class="size-4 shrink-0" />
          <span class="group-data-[collapsible=icon]:group-data-[state=collapsed]:hidden">
            {t.menu_logout}
          </span>
        </Sidebar.MenuButton>
      </Sidebar.MenuItem>
    </Sidebar.Menu>
  </Sidebar.Footer>
</Sidebar.Root>

{#snippet SiteButton(site: any, showArrow = true)}
  <Sidebar.MenuButton
    size="lg"
    class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
  >
    <div class="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-xl group-data-[state=collapsed]:mx-auto">
      {site?.name?.[0] || 'S'}
    </div>
    <div class="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:group-data-[state=collapsed]:hidden ml-1">
      <span class="truncate font-semibold">{site?.name || t.select_site}</span>
      <span class="truncate text-[10px] text-muted-foreground uppercase">{currentTenant?.plan_level || t.plan_free}</span>
    </div>
    {#if showArrow}
      <ChevronsUpDown class="ml-auto size-4 shrink-0 group-data-[collapsible=icon]:group-data-[state=collapsed]:hidden opacity-50" />
    {/if}
  </Sidebar.MenuButton>
{/snippet}